<template>
  <b-card no-body>
    <b-card-body>
      <!-- Filters -->
      <my-orders-filter :values="filterValues" :filter-submitting="isLoading" @filter="setFilter" />

      <!-- Table -->
      <div class="table-responsive">
        <vue-good-table
          style="min-width: 100%"
          mode="remote"
          :sort-options="{ enabled: false }"
          :total-rows="totalRecords"
          :is-loading.sync="isLoading"
          :show-footer="true"
          :pagination-options="
            getPaginationOptions(filter.limit, filter.page, {
              ofLabel: `/ \u00A0`,
              rowsPerPageLabel: `${$t(
                'Total number of records'
              )}: ${totalRecords}\u00A0\u00A0\u00A0\u00A0 | \u00A0\u00A0\u00A0\u00A0${$t(
                'Rows per page'
              )}`
            })
          "
          :rows="rows"
          :columns="columns"
          @on-page-change="onPageChange"
          @on-per-page-change="onPerPageChange"
        >
          <!-- Custom Column Templates -->
          <template slot="table-row" slot-scope="props">
            <!-- Sipariş Numarası -->
            <span v-if="props.column.field === 'orderNo'">
              <span class="text-primary font-weight-bolder">{{ props.row.orderNo }}</span>
            </span>

            <!-- Alıcı Adı -->
            <span v-else-if="props.column.field === 'recipientName'">
              <span class="font-weight-bold">{{ props.row.recipientName }}</span>
            </span>

            <!-- Ürün(ler) -->
            <span v-else-if="props.column.field === 'products'">
              <div v-for="(product, index) in props.row.products" :key="index" class="text-nowrap">
                {{ product }}
              </div>
            </span>

            <!-- Durum -->
            <span v-else-if="props.column.field === 'status'">
              <b-badge
                pill
                class="px-2"
                :variant="props.row.status === 'Active' ? 'light-success' : 'light-danger'"
              >
                {{ $t(props.row.status) }}
              </b-badge>
            </span>

            <!-- Sevkiyat Oluşturuldu -->
            <span v-else-if="props.column.field === 'shipmentCreated'">
              <b-badge
                pill
                class="px-2"
                :variant="
                  props.row.shipmentCreated === 'Consignment Created'
                    ? 'light-success'
                    : 'light-secondary'
                "
              >
                {{ $t(props.row.shipmentCreated) }}
              </b-badge>
            </span>

            <!-- İşlem -->
            <span v-else-if="props.column.field === 'action'">
              <b-button
                v-if="props.row.action === 'Create Label'"
                variant="primary"
                size="sm"
                @click="openLabelModal(props.row)"
              >
                {{ $t(props.row.action) }}
              </b-button>
              <b-button
                v-else-if="props.row.action === 'View Consignment'"
                variant="success"
                size="sm"
                class="font-weight-bold"
                :disabled="!props.row.raw.has_consignment || !props.row.raw.consignment_id"
                @click="viewConsignment(props.row.raw.consignment_id)"
              >
                <feather-icon icon="EyeIcon" size="14" class="mr-25" />
                {{ $t(props.row.action) }}
              </b-button>
              <span
                v-else-if="props.row.action === 'Order Cancelled'"
                class="text-danger font-italic font-weight-bold"
                style="font-size: 0.9em"
              >
                {{ $t(props.row.action) }}
              </span>
            </span>

            <!-- Default -->
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </div>
    </b-card-body>
    <create-label-modal ref="labelModal" :order="selectedOrder" @label-created="fetchOrders" />
  </b-card>
</template>

<script>
import { BCard, BCardBody, BButton, BBadge } from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import { vueGoodTablePagination } from '@core/mixins/vueGoodTablePagination';
import useService from '@/auth/service/useService';
import MyOrdersFilter from './MyOrdersFilter.vue';
import CreateLabelModal from './components/CreateLabelModal.vue';

export default {
  name: 'MyOrders',
  components: {
    BCard,
    BCardBody,
    BButton,
    BBadge,
    VueGoodTable,
    MyOrdersFilter,
    CreateLabelModal
  },
  mixins: [vueGoodTablePagination],
  data() {
    return {
      isLoading: false,
      selectedOrder: null,
      filterValues: {
        search: '',
        country: null,
        shipmentStatus: null,
        orderStatus: null,
        dateFrom: null,
        dateTo: null
      },
      filter: {
        page: 1,
        limit: 10
      },
      columns: [
        { label: this.$t('Order Number'), field: 'orderNo' },
        { label: this.$t('Recipient Name'), field: 'recipientName' },
        { label: this.$t('Recipient Country'), field: 'recipientCountry' },
        { label: this.$t('Products'), field: 'products' },
        { label: this.$t('Date'), field: 'date' },
        { label: this.$t('Status'), field: 'status' },
        { label: this.$t('Shipment'), field: 'shipmentCreated' },
        { label: this.$t('Action'), field: 'action', sortable: false }
      ],
      rows: [],
      totalRows: 0
    };
  },
  computed: {
    totalRecords() {
      return this.totalRows;
    }
  },
  mounted() {
    this.fetchOrders();
  },
  methods: {
    async fetchOrders() {
      try {
        this.isLoading = true;
        let startDate;
        let endDate;

        if (this.filterValues.dateRange) {
          const separator = this.filterValues.dateRange.includes(' to ') ? ' to ' : ' - ';
          const parts = this.filterValues.dateRange.split(separator);
          startDate = parts[0] || undefined;
          endDate = parts[1] || parts[0] || undefined;
        }
        const params = {
          limit: this.filter.limit,
          page: this.filter.page,
          q: this.filterValues.search || undefined,
          country: this.filterValues.country?.value || this.filterValues.country || undefined,
          has_consignment:
            this.filterValues.shipmentStatus !== undefined &&
            this.filterValues.shipmentStatus !== null &&
            this.filterValues.shipmentStatus !== ''
              ? this.filterValues.shipmentStatus
              : undefined,
          status:
            this.filterValues.orderStatus !== undefined &&
            this.filterValues.orderStatus !== null &&
            this.filterValues.orderStatus !== ''
              ? this.filterValues.orderStatus
              : undefined,
          date_from: startDate,
          date_to: endDate
        };

        const response = await useService.getCoupangOrders(params);
        if (response && response.status && response.data) {
          const { items, total } = response.data;
          this.totalRows = total || 0;

          this.rows = items.map(item => {
            let actionText = 'Create Label';
            if (item.cancelled_at) {
              actionText = 'Order Cancelled';
            } else if (item.has_consignment) {
              actionText = 'View Consignment';
            }

            let statusText = item.status;
            if (item.status === 'INSTRUCT' || item.status === 'ACCEPT') {
              statusText = 'Active';
            }

            return {
              orderNo: item.order_id,
              recipientName: item.buyer_name,
              recipientCountry: item.buyer_country || '-',
              products: item.products ? item.products.map(p => `${p.name} x ${p.quantity}`) : [],
              date: this.formatDate(item.ordered_at),
              status: statusText,
              shipmentCreated: item.has_consignment
                ? 'Consignment Created'
                : 'Consignment Not Created',
              action: actionText,
              raw: item
            };
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(this.$t('Error Fetching Orders'), error);
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    setFilter(values) {
      this.filterValues = { ...values };
      this.filter.page = 1;
      this.fetchOrders();
    },
    onPageChange(params) {
      this.filter.page = params.currentPage;
      this.fetchOrders();
    },
    onPerPageChange(params) {
      this.filter.page = 1;
      this.filter.limit = params.currentPerPage;
      this.fetchOrders();
    },
    openLabelModal(order) {
      this.selectedOrder = order;
      this.$nextTick(() => {
        this.$refs.labelModal.show();
      });
    },
    viewConsignment(consignmentId) {
      if (!consignmentId) return;
      const routeData = this.$router.resolve({
        name: 'consignments_detail',
        params: { id: consignmentId }
      });
      window.open(routeData.href, '_blank');
    }
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>

<style lang="scss" scoped>
.text-nowrap {
  white-space: nowrap;
}
</style>
